import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../context/CompanyContext';
import { ChartBarStacked } from '../../Charts/ChartBarStacked';
import { ChartConfig } from '../../../common/shadcn/chart';
import { DashboardQueries, MetricsResponse, MetricsVariables } from '../../../../graphql/dashboard.graphql';
import MetricResult, { MetricResultType } from '../../../../types/metrics/metricResult';
import useQuery from '../../../../hooks/useQuery';
import { ColorUtil } from '../../../../util/color.util';
import { MetricEntityType, MetricFilter } from '../../../../types/metrics/metricFilter';
import { CompanyConnectionContext } from '../../../../context/CompanyConnectionContext';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { Dashboard, DashboardContext } from '../../../../context/DashboardContext';
import { UserContext } from '../../../../context/UserContext';

interface MetricChartBarStackedProps {
  filter: MetricFilter;
  type: MetricEntityType;
  title: string;
}

export default function MetricChartBarStacked({ filter, type: entityType, title }: MetricChartBarStackedProps) {
  const { companies, currentCompany } = useContext(CompanyContext);
  const { companyConnections } = useContext(CompanyConnectionContext);
  const { cache, setCache } = useContext(DashboardContext);
  const { locale } = useContext(UserContext);

  const [data, setData] = useState<MetricResult[]>([]);
  const [type, setType] = useState<MetricResultType>(MetricResultType.company);

  const hasCompanyConnections = Array.from(companyConnections.values()).some(cc => cc.parentId === currentCompany.id);
  const connections = new Set(
    Array.from(companyConnections.values())
      .filter(cc => cc.parentId === currentCompany.id)
      .map(cc => cc.companyId),
  );

  const cacheKey = Dashboard.getCacheKey(entityType, filter);
  const hasCachedData = cache.has(cacheKey);

  const { loading, refetch } = useQuery<MetricsResponse, MetricsVariables>(DashboardQueries.metrics, {
    variables: { filter, type: entityType },
    skip: hasCachedData,
    onCompleted: res => {
      const { data, type } = MetricResult.toChartBarStackedData(res.metrics, filter.interval, locale);

      cache.set(cacheKey, { data, type });
      setCache(new Map(cache));

      setData(data);
      setType(type);
    },
  });

  useEffect(() => {
    if (hasCachedData && cache.has(cacheKey)) {
      const { data: cachedData, type: cachedType } = cache.get(cacheKey)!;
      setData(cachedData);
      setType(cachedType);
    }
  }, [cache, filter]);

  const companyTypeConfig = () => {
    const items = hasCompanyConnections
      ? Array.from(companies.values()).filter(c => connections.has(c.id) || c.id === currentCompany.id)
      : [currentCompany];

    const object: any = {};
    const shades = ColorUtil.generateShades(currentCompany.settings.primaryColor, items.length);

    items.forEach((company, index) => {
      object[company.id] = {
        label: company.name,
        color: company.settings.primaryColor,
      };
    });

    return object as ChartConfig;
  };

  const typeConfig = () => {
    const items = Array.from(new Set(Object.keys(MetricEntityType)))
      .filter(item => (data[0] as any)[item] !== undefined)
      .sort((a, b) => {
        return a.localeCompare(b);
      });

    const company = filter.companyIds.length ? companies.get(filter.companyIds[0]) || currentCompany : currentCompany;

    const object: any = {};
    const shades =
      items.length === 1
        ? ColorUtil.generateShades(company.settings.primaryColor, items.length)
        : [
            ColorUtil.generateShades(company.settings.primaryColor, items.length - 1).splice(1, items.length - 1),
            company.settings.primaryColor,
            ColorUtil.generateShades(company.settings.accentColor, 1),
          ].flat();

    items.forEach((type, index) => {
      object[type] = {
        label: t(type as TranslationKey).plural.label,
        color: shades[index],
      };
    });

    return object as ChartConfig;
  };

  const chartConfig = useMemo(() => {
    if (type === MetricResultType.company) {
      return companyTypeConfig();
    } else {
      return typeConfig();
    }
  }, [companies, type, data]);

  return (
    <ChartBarStacked
      loading={loading}
      title={title}
      config={chartConfig}
      data={data}
      xAxis='label'
      tickFormatter={value => value}
    />
  );
}
